import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DealerLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  region: string;
  postal_code: string;
  phone: string;
  latitude: number;
  longitude: number;
  recall_count: number;
  skill_level: number;
}

interface Technician {
  id: string;
  full_name: string;
  skill_level: number;
  phone_number: string;
  email: string;
  home_base?: {
    latitude: number;
    longitude: number;
  };
}

interface RouteStop {
  id: string;
  dealer: DealerLocation;
  arrival_time: string;
  departure_time: string;
  service_duration: number;
  stop_order: number;
}

interface TechnicianRoute {
  technician_id: string;
  technician: Technician;
  stops: RouteStop[];
  total_distance: number;
  total_duration: number;
  route_color: string;
}

interface RouteConfirmationData {
  technician_assignments: Array<{
    technician_id: string;
    vin: string;
    campaign_number: string;
    dealer_name: string;
    estimated_duration_minutes: number;
    notes?: string;
  }>;
  route_optimization_data: {
    total_distance: number;
    total_duration: number;
    optimization_applied: boolean;
  };
  confirmed_by: string;
  confirmation_date: string;
}

export const useRouteConfirmation = () => {
  const [routes, setRoutes] = useState<TechnicianRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { toast } = useToast();

  // Confirm routes and save to database
  const confirmRoutes = useCallback(
    async (routeData: TechnicianRoute[]) => {
      setIsConfirming(true);
      try {
        // Get current user
        const {
          data: { user },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error('User not authenticated');
        }

        // Prepare confirmation data
        const confirmationData: RouteConfirmationData = {
          technician_assignments: routeData.flatMap(route =>
            route.stops.map(stop => ({
              technician_id: route.technician_id,
              vin: stop.dealer.id, // Using dealer ID as VIN placeholder
              campaign_number: `ROUTE-${Date.now()}`,
              dealer_name: stop.dealer.name,
              estimated_duration_minutes: stop.service_duration,
              notes: `Route stop ${stop.stop_order}`
            }))
          ),
          route_optimization_data: {
            total_distance: routeData.reduce((sum, route) => sum + route.total_distance, 0),
            total_duration: routeData.reduce((sum, route) => sum + route.total_duration, 0),
            optimization_applied: false
          },
          confirmed_by: user.id,
          confirmation_date: new Date().toISOString()
        };

        // Save to route_confirmations table (if it exists)
        // For now, we'll save to technician_assignments with a confirmed status
        const assignments = confirmationData.technician_assignments.map(assignment => ({
          technician_id: assignment.technician_id,
          vin: assignment.vin,
          campaign_number: assignment.campaign_number,
          recall_title: `Route confirmation for ${assignment.dealer_name}`,
          dealer_id: '',
          dealer_name: assignment.dealer_name,
          assignment_status: 'confirmed' as const,
          priority: 'medium' as const,
          estimated_duration_minutes: assignment.estimated_duration_minutes,
          notes: assignment.notes || '',
          assigned_by: user.id,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        // Insert assignments
        const { error: insertError } = await supabase
          .from('technician_assignments')
          .insert(assignments);

        if (insertError) {
          throw insertError;
        }

        // Send notifications to technicians
        await notifyTechnicians(routeData);

        toast({
          title: 'Routes Confirmed',
          description: `Successfully confirmed routes for ${routeData.length} technicians`
        });

        return { success: true };
      } catch (error) {
        toast({
          title: 'Confirmation Failed',
          description: error instanceof Error ? error.message : 'Failed to confirm routes',
          variant: 'destructive'
        });
        throw error;
      } finally {
        setIsConfirming(false);
      }
    },
    [toast]
  );

  // Optimize a single route
  const optimizeRoute = useCallback(
    async (route: TechnicianRoute) => {
      setLoading(true);
      try {
        // Simple optimization: sort stops by distance from technician home base
        if (route.technician.home_base && route.stops.length > 1) {
          const optimizedStops = [...route.stops].sort((a, b) => {
            const distanceA = calculateDistance(
              route.technician.home_base!.latitude,
              route.technician.home_base!.longitude,
              a.dealer.latitude,
              a.dealer.longitude
            );
            const distanceB = calculateDistance(
              route.technician.home_base!.latitude,
              route.technician.home_base!.longitude,
              b.dealer.latitude,
              b.dealer.longitude
            );
            return distanceA - distanceB;
          });

          // Update stop orders
          const optimizedRoute = {
            ...route,
            stops: optimizedStops.map((stop, index) => ({
              ...stop,
              stop_order: index + 1
            }))
          };

          // Recalculate total distance
          const totalDistance = calculateRouteDistance(optimizedRoute.stops);
          optimizedRoute.total_distance = totalDistance;

          // Update routes state
          setRoutes(prev =>
            prev.map(r => (r.technician_id === route.technician_id ? optimizedRoute : r))
          );

          toast({
            title: 'Route Optimized',
            description: `Optimized route for ${route.technician.full_name}`
          });

          return optimizedRoute;
        }

        return route;
      } catch (error) {
        toast({
          title: 'Optimization Failed',
          description: error instanceof Error ? error.message : 'Failed to optimize route',
          variant: 'destructive'
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Send notifications to technicians
  const notifyTechnicians = useCallback(async (routes: TechnicianRoute[]) => {
    try {
      // In a real implementation, this would send emails/SMS to technicians
      // For now, we'll just log the notifications
      // TODO: Implement actual notification sending
      // - Email notifications with route details
      // - SMS notifications for mobile technicians
      // - Push notifications for mobile app users
    } catch (error) {
      // Don't throw here - notifications are not critical for route confirmation
    }
  }, []);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate total route distance
  const calculateRouteDistance = (stops: RouteStop[]): number => {
    if (stops.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 0; i < stops.length - 1; i++) {
      const current = stops[i];
      const next = stops[i + 1];
      if (current && next) {
        totalDistance += calculateDistance(
          current.dealer.latitude,
          current.dealer.longitude,
          next.dealer.latitude,
          next.dealer.longitude
        );
      }
    }

    return totalDistance;
  };

  return {
    routes,
    loading,
    isConfirming,
    confirmRoutes,
    optimizeRoute,
    notifyTechnicians
  };
};
